import { useEffect, useRef, useState } from 'react';

/**
 * Screen Wake Lock while a component is mounted (the full-screen QR card).
 * Uses the real Wake Lock API where available; falls back to just telling
 * the caller it isn't supported so the UI can suggest raising brightness
 * manually instead. Never claims to control brightness — the Web Platform
 * has no API for that, and the spec explicitly says not to imply one.
 */
export function useWakeLock(active: boolean) {
  const [supported] = useState(() => typeof navigator !== 'undefined' && 'wakeLock' in navigator);
  const [held, setHeld] = useState(false);
  const lockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active || !supported) return;
    let cancelled = false;

    async function acquire() {
      try {
        const lock = await (navigator as Navigator & { wakeLock: WakeLock }).wakeLock.request('screen');
        if (cancelled) {
          await lock.release();
          return;
        }
        lockRef.current = lock;
        setHeld(true);
        lock.addEventListener('release', () => setHeld(false));
      } catch {
        setHeld(false);
      }
    }

    void acquire();

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && active && !lockRef.current) void acquire();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      lockRef.current?.release().catch(() => undefined);
      lockRef.current = null;
      setHeld(false);
    };
  }, [active, supported]);

  return { supported, held };
}

// Minimal ambient types for browsers/TS libs without WakeLock typings yet.
interface WakeLockSentinel extends EventTarget {
  release(): Promise<void>;
}
interface WakeLock {
  request(type: 'screen'): Promise<WakeLockSentinel>;
}
